/**
 * Pure helpers for smart NDA template-field suggestion: text-hit
 * keywords + which PDF pages to send to vision.
 *
 * Prefer signature/execution pages; fall back to page 1 + closing.
 * Cap stays at 2 pages so vision cost stays in the cents.
 */
export const TEMPLATE_FIELD_HINT_PATTERN = /(?:\b(?:signature|sign\s+here|signed\s+by|party\s*a|party\s*b|authorised\s+signat|authorized\s+signat|witness|print\s+name|legal\s+name|registered\s+(?:office|address)|company\s+number|date\s+of\s+(?:signature|agreement)|agreement\s+date)\b|^\s*(?:name|title|date|signature|legal\s+name|registered\s+address)\s*:)/i;
/**
 * Prefer signature/execution pages for vision (cover→body→signature NDAs).
 * Fall back to page 1 + last hit page when no execution hits exist.
 * Cap at 2 pages for vision cost.
 */
export function pickTemplateVisionPages(numPages, hits, maxPages = 2) {
    if (numPages < 1)
        return [];
    const capped = Math.max(1, Math.min(maxPages, 2));
    const hitPages = [...new Set(hits.map((h) => h.page))]
        .filter((p) => p >= 1 && p <= numPages)
        .sort((a, b) => a - b);
    const executionPages = [
        ...new Set(hits
            .filter((h) => /\b(?:signature|sign\s+here|execution|signed\s+by|authorised\s+signat|authorized\s+signat)\b/i.test(h.text ?? ""))
            .map((h) => h.page)),
    ]
        .filter((p) => p >= 1 && p <= numPages)
        .sort((a, b) => a - b);
    if (capped === 1) {
        return [executionPages.at(-1) ?? hitPages.at(-1) ?? numPages];
    }
    if (numPages === 1)
        return [1];
    if (executionPages.length > 0) {
        const closing = executionPages.at(-1);
        const priorExec = executionPages.length > 1 ? executionPages[executionPages.length - 2] : null;
        const coverHit = hitPages.find((p) => p !== closing &&
            hits.some((h) => h.page === p &&
                /\b(?:legal\s+name|party\s*b|registered\s+address|agreement\s+date)\b/i.test(h.text ?? "")));
        const secondary = priorExec ?? coverHit ?? (closing === 1 ? numPages : 1);
        if (secondary === closing)
            return [closing];
        return [secondary, closing].sort((a, b) => a - b);
    }
    const closing = hitPages.length > 0 ? (hitPages.at(-1) ?? numPages) : numPages;
    if (closing === 1) {
        return numPages > 1 ? [1, numPages] : [1];
    }
    return [1, closing];
}
export function isTemplateFieldHintText(text) {
    return TEMPLATE_FIELD_HINT_PATTERN.test(text);
}
//# sourceMappingURL=template-field-suggest.js.map